/**
 * Linked notes panel for the essay detail Supporting Info drawer.
 *
 * Shows standalone notes linked to the essay via noteLinks (both manual
 * and auto-linked). Allows manually linking/unlinking notes from the
 * essay side.
 *
 * Called by:
 * - EssayDetailPage (Supporting Info drawer, Notes tab)
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Modal,
  Input,
  List,
  Tag,
  Space,
  Typography,
  Popconfirm,
  message,
  Empty,
  Spin,
} from 'antd';
import {
  LinkOutlined,
  CheckOutlined,
  SearchOutlined,
  DisconnectOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNotes, linkNoteToEssay, unlinkNoteFromEssay } from '../services/api';
import type { LinkedNoteFromEssay, User, Note } from '@compendium/shared';

const { Title, Text } = Typography;

// =============================================================================
// Props
// =============================================================================

interface LinkedNotesPanelProps {
  slug: string;
  linkedNotes: LinkedNoteFromEssay[];
  isLoading?: boolean;
  user: User;
  onNotesChange?: () => void;
}

// =============================================================================
// Component
// =============================================================================

export default function LinkedNotesPanel({
  slug,
  linkedNotes,
  isLoading = false,
  user,
  onNotesChange,
}: LinkedNotesPanelProps) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isAuthor = user.role === 'author';

  // Fetch all notes only when modal is open
  const { data: allNotes = [], isLoading: notesLoading } = useQuery<Note[]>({
    queryKey: ['allNotes'],
    queryFn: () => getNotes(),
    enabled: modalOpen,
  });

  // Link mutation
  const linkMutation = useMutation({
    mutationFn: (noteId: number) => linkNoteToEssay(slug, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedNotes', slug] });
      onNotesChange?.();
      message.success('Note linked');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to link note');
    },
  });

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: (noteId: number) => unlinkNoteFromEssay(slug, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['linkedNotes', slug] });
      onNotesChange?.();
      message.success('Note unlinked');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to unlink note');
    },
  });

  // IDs already linked
  const linkedIds = useMemo(() => new Set(linkedNotes.map(n => n.id)), [linkedNotes]);

  // Filtered notes for the modal
  const filteredNotes = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return allNotes;
    return allNotes.filter(n => n.title.toLowerCase().includes(term));
  }, [allNotes, search]);

  function openModal() {
    setSearch('');
    setModalOpen(true);
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Linked Notes
        </Title>
        {isAuthor && (
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={openModal}
            size="small"
          >
            Link Note
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin tip="Loading notes..." />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && linkedNotes.length === 0 && (
        <Empty
          description="No linked notes"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Notes list */}
      {!isLoading && linkedNotes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linkedNotes.map(note => (
            <div
              key={note.id}
              style={{
                background: '#242424',
                borderRadius: 8,
                padding: '12px 16px',
                border: '1px solid #333',
              }}
            >
              {/* Title row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link
                    to={`/reviewer/notes/${note.id}`}
                    style={{ color: '#d4af37', fontWeight: 500, fontSize: 14 }}
                  >
                    {note.title} <ExportOutlined style={{ fontSize: 11 }} />
                  </Link>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {note.notebookName}
                    </Text>
                  </div>
                </div>

                <Space size={4}>
                  {note.source === 'auto' && note.similarity != null ? (
                    <Tag color="processing" style={{ margin: 0, fontSize: 11 }}>
                      {Math.round(note.similarity * 100)}% match
                    </Tag>
                  ) : (
                    <Tag style={{ margin: 0, fontSize: 11, color: '#888', borderColor: '#444' }}>
                      manual
                    </Tag>
                  )}
                </Space>
              </div>

              {/* Summary */}
              {note.autoSummary && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" italic style={{ fontSize: 13 }}>
                    {note.autoSummary}
                  </Text>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Updated {new Date(note.updatedAt).toLocaleDateString()}
                </Text>

                {isAuthor && note.source === 'manual' && (
                  <Popconfirm
                    title="Unlink this note?"
                    onConfirm={() => unlinkMutation.mutate(note.id)}
                    okText="Unlink"
                    cancelText="Cancel"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DisconnectOutlined />}
                      loading={unlinkMutation.isPending}
                      style={{ fontSize: 12 }}
                    >
                      Unlink
                    </Button>
                  </Popconfirm>
                )}
                {note.source === 'auto' && (
                  <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                    Auto-linked by similarity
                  </Text>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link Note modal */}
      <Modal
        title="Link Note to Essay"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Input
          placeholder="Filter notes..."
          prefix={<SearchOutlined style={{ color: '#666' }} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          allowClear
          style={{ marginBottom: 12 }}
        />

        {notesLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <List
            dataSource={filteredNotes}
            locale={{ emptyText: 'No notes found' }}
            style={{ maxHeight: 400, overflowY: 'auto' }}
            renderItem={(note: Note) => {
              const alreadyLinked = linkedIds.has(note.id);
              return (
                <List.Item
                  style={{
                    cursor: alreadyLinked ? 'default' : 'pointer',
                    opacity: alreadyLinked ? 0.5 : 1,
                    padding: '10px 12px',
                  }}
                  onClick={() => {
                    if (!alreadyLinked) linkMutation.mutate(note.id);
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    {alreadyLinked && <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />}
                    <span>{note.title}</span>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </div>
  );
}
